(() => {
  // GH-6320 nav a11y + active Categories backfill (deploy stamp 2026-06-29T19:22Z)
  const prefersReducedMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  );

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) =>
    Array.from(root.querySelectorAll(selector));

  const toggleClass = (el, className, force) => {
    if (!el) return;
    if (typeof force === "boolean") {
      el.classList.toggle(className, force);
      return;
    }
    el.classList.toggle(className);
  };

  // GH #2123 / OPP-033: After OTP login, returning via browser back/forward cache
  // (bfcache) can leave the pre-login page in a stale, unauthenticated state.
  // Two complementary mechanisms:
  //  1. Login-click marker: when a login link is clicked, set a sessionStorage flag;
  //     on bfcache restoration, reload once.  (Original GH #2123 fix.)
  //  2. Auth-state stamp: on every pageshow, compare the current
  //     data-pma-customer-auth value from the DOM with the previously stored value
  //     in sessionStorage; on bfcache restoration, if they differ, reload. In all
  //     cases, persist the current value back to sessionStorage so future restores
  //     see the latest auth state. This handles ALL bfcache pages (not just the
  //     first) and also covers the logout→bfcache reverse scenario.

  const isBfcacheOrBackForward = (event) => {
    const isBfcache = Boolean(event && event.persisted);
    let isBackForward = false;
    try {
      const entries = performance.getEntriesByType?.("navigation") || [];
      const entry = entries?.[0];
      isBackForward = Boolean(entry && entry.type === "back_forward");
    } catch {
      isBackForward = false;
    }
    return isBfcache || isBackForward;
  };

  // --- Mechanism 1: login-click marker ---
  const LOGIN_BFCACHE_REFRESH_KEY = "pma_login_bfcache_refresh_v1";
  const isLoginHref = (href) => {
    const h = String(href || "");
    if (!h) return false;
    return h.includes("/account/login") || h.includes("/customer_authentication/login");
  };
  const markLoginNavForBfcacheRefresh = () => {
    document.addEventListener(
      "click",
      (e) => {
        const a = e && e.target && e.target.closest ? e.target.closest("a[href]") : null;
        if (!a) return;
        const href = a.getAttribute("href") || a.href || "";
        if (!isLoginHref(href)) return;
        try {
          sessionStorage.setItem(LOGIN_BFCACHE_REFRESH_KEY, "1");
        } catch {
          // ignore sessionStorage failures
        }
      },
      true,
    );
  };
  const reloadIfBfcacheAfterLogin = () => {
    window.addEventListener("pageshow", (event) => {
      let marker = "";
      try {
        marker = sessionStorage.getItem(LOGIN_BFCACHE_REFRESH_KEY) || "";
      } catch {
        marker = "";
      }
      if (marker !== "1") return;

      if (!isBfcacheOrBackForward(event)) return;

      try {
        sessionStorage.removeItem(LOGIN_BFCACHE_REFRESH_KEY);
      } catch {
        // ignore sessionStorage failures
      }
      window.location.reload();
    });
  };

  // --- Mechanism 2: auth-state stamp (OPP-033) ---
  const AUTH_STATE_STORAGE_KEY = "pma_auth_state_v1";
  const syncAuthStateOnBfcache = () => {
    window.addEventListener("pageshow", (event) => {
      const bodyEl = document.body;
      const domAuth = bodyEl ? bodyEl.getAttribute("data-pma-customer-auth") || "" : "";

      if (isBfcacheOrBackForward(event)) {
        let storedAuth = "";
        try {
          storedAuth = sessionStorage.getItem(AUTH_STATE_STORAGE_KEY) || "";
        } catch {
          storedAuth = "";
        }
        if (storedAuth !== "" && storedAuth !== domAuth) {
          window.location.reload();
          return;
        }
      }

      // Update stamp so future restores see the latest auth state.
      try {
        sessionStorage.setItem(AUTH_STATE_STORAGE_KEY, domAuth);
      } catch {
        // ignore sessionStorage failures
      }
    });
  };

  // F06.3: Producer referral persistence (no PII).
  // Captures referral context from URL params and keeps it available for join + cart attribution.
  const REFERRAL_STORAGE_KEY = "pma_referral_ctx_v1";
  const REFERRAL_CART_SYNC_KEY = "pma_referral_cart_synced_v1";
  const REFERRAL_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

  const normalizeSlug = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    // Keep URL-safe slug characters only (a-z, 0-9, dash). Collapse runs of dashes.
    const cleaned = raw
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
    return cleaned.slice(0, 80);
  };

  const readReferralFromUrl = () => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const producer =
        normalizeSlug(params.get("pma_ref_producer") || params.get("pma_producer") || params.get("pma_ref") || "");
      const campaign = String(params.get("pma_campaign") || params.get("pma_ref_campaign") || "").trim().slice(0, 120);
      const source = String(params.get("pma_ref_src") || params.get("pma_ref_source") || "").trim().slice(0, 120);
      return { producer, campaign, source };
    } catch {
      return { producer: "", campaign: "", source: "" };
    }
  };

  const loadReferralCtx = () => {
    try {
      const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const capturedAt = Number(parsed.captured_at || 0);
      if (!capturedAt || Date.now() - capturedAt > REFERRAL_TTL_MS) return null;
      const producer = normalizeSlug(parsed.producer || "");
      if (!producer) return null;
      return {
        producer,
        campaign: String(parsed.campaign || "").trim().slice(0, 120),
        source: String(parsed.source || "").trim().slice(0, 120),
        captured_at: capturedAt
      };
    } catch {
      return null;
    }
  };

  const saveReferralCtx = (ctx) => {
    try {
      if (!ctx || !ctx.producer) return;
      localStorage.setItem(
        REFERRAL_STORAGE_KEY,
        JSON.stringify({
          producer: ctx.producer,
          campaign: ctx.campaign || "",
          source: ctx.source || "",
          captured_at: Date.now()
        })
      );
    } catch {
      // ignore storage failures
    }
  };

  const captureReferral = () => {
    const fromUrl = readReferralFromUrl();
    if (fromUrl.producer) saveReferralCtx(fromUrl);
  };

  const syncReferralToCart = async () => {
    const ctx = loadReferralCtx();
    if (!ctx || !ctx.producer) return;
    try {
      if (sessionStorage.getItem(REFERRAL_CART_SYNC_KEY) === "1") return;
      sessionStorage.setItem(REFERRAL_CART_SYNC_KEY, "1");
    } catch {
      // no sessionStorage; continue best-effort
    }

    const attributes = { pma_ref_producer: ctx.producer };
    if (ctx.campaign) attributes.pma_campaign = ctx.campaign;
    if (ctx.source) attributes.pma_ref_src = ctx.source;

    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes })
      });
    } catch {
      // ignore network failures; attribution is best-effort
    }
  };

  const enhanceJoinLinksWithReferral = () => {
    document.addEventListener(
      "click",
      function (e) {
        const a = e.target && e.target.closest && e.target.closest('a[data-pma-cta="join"]');
        if (!a) return;
        const ctx = loadReferralCtx();
        if (!ctx || !ctx.producer) return;
        try {
          const url = new URL(a.getAttribute("href") || a.href || "", window.location.origin);
          if (!url.searchParams.get("pma_ref_producer")) url.searchParams.set("pma_ref_producer", ctx.producer);
          if (ctx.campaign && !url.searchParams.get("pma_campaign")) url.searchParams.set("pma_campaign", ctx.campaign);
          if (ctx.source && !url.searchParams.get("pma_ref_src")) url.searchParams.set("pma_ref_src", ctx.source);
          // If no changes, allow default navigation.
          const next = url.toString();
          if ((a.href || "") === next) return;
          e.preventDefault();
          window.location.assign(next);
        } catch {
          // ignore URL parsing errors
        }
      },
      true
    );
  };
  // Product search entry points keep /search as the no-JS fallback. With JS,
  // route the submitted query into the canonical Marketplace catalog URL.
  const setupCanonicalCatalogSearchEntryRouting = () => {
    if (document.documentElement.dataset.pmaCatalogSearchEntryBound === "1") return;
    document.documentElement.dataset.pmaCatalogSearchEntryBound = "1";
    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target?.closest?.("form[data-pma-catalog-search-entry]");
        if (!form) return;
        const queryInput = form.querySelector('input[name="q"]');
        const query = String(queryInput?.value || "").trim();
        const current = new URL(window.location.href);
        const onCatalog = String(current.pathname || "").replace(/\/+$/, "") === "/collections/all";
        if (!query && (!onCatalog || !String(current.searchParams.get("q") || "").trim())) return;

        event.preventDefault();
        const target = new URL("/collections/all", window.location.origin);

        // Canonical catalog state is cumulative:
        // a new keyword search keeps the currently active facets and sort.
        // Pagination is intentionally reset because the result set changed.
        if (onCatalog) {
          current.searchParams.forEach((value, name) => {
            if (String(name || "").startsWith("filter.")) {
              target.searchParams.append(name, value);
            }
          });

          const currentSort = String(
            current.searchParams.get("sort_by") || "",
          ).trim();

          if (currentSort) {
            target.searchParams.set("sort_by", currentSort);
          }
        }

        const preview = current.searchParams.get("preview_theme_id");
        if (preview) target.searchParams.set("preview_theme_id", preview);

        target.searchParams.delete("page");

        if (query) {
          target.searchParams.set("q", query);
          target.hash = "#shop-products";
        }
        const visible = target.pathname + target.search + target.hash;
        const catalogRouter = window.PMA?.catalogSearch?.navigate;
        if (onCatalog && typeof catalogRouter === "function") {
          catalogRouter(visible, { historyMode: "push", scroll: true });
          return;
        }
        window.location.assign(visible);
      },
      true,
    );
  };


  captureReferral();
  markLoginNavForBfcacheRefresh();
  reloadIfBfcacheAfterLogin();
  syncAuthStateOnBfcache();
  enhanceJoinLinksWithReferral();
  setupCanonicalCatalogSearchEntryRouting();
  // Best-effort: keep cart attribution updated after referral capture.
  syncReferralToCart();

  // Overlay scroll lock (nav/search drawers). Depth counter prevents conflicts.
  let overlayDepth = 0;
  let overlayScrollY = 0;
  const lockOverlay = () => {
    if (overlayDepth === 0) {
      overlayScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add("is-overlay-open");
      document.body.classList.add("is-overlay-open");
      // Preserve scroll position while preventing background scroll.
      document.body.style.position = "fixed";
      document.body.style.top = `-${overlayScrollY}px`;
      document.body.style.width = "100%";
    }
    overlayDepth += 1;
  };
  const unlockOverlay = () => {
    if (overlayDepth <= 0) return;
    overlayDepth -= 1;
    if (overlayDepth !== 0) return;
    document.documentElement.classList.remove("is-overlay-open");
    document.body.classList.remove("is-overlay-open");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    const restoreScrollY = overlayScrollY;
    const restoreScroll = () => {
      window.scrollTo(0, restoreScrollY);
    };
    restoreScroll();
    window.requestAnimationFrame?.(() => {
      restoreScroll();
      window.requestAnimationFrame?.(restoreScroll);
    });
  };

  // Color mode (dark mode) — JS enhancement only.
  // No-JS behavior: `data-color-mode` is set by Liquid and CSS follows.
  const COLOR_MODE_STORAGE_KEY = "pma_color_mode";
  const normalizeColorMode = (value) => {
    if (value === "light" || value === "dark" || value === "system") return value;
    return "system";
  };
  const prefersDarkScheme = () =>
    window.matchMedia?.("(prefers-color-scheme: dark)")?.matches === true;
  const getColorMode = () =>
    normalizeColorMode(document.documentElement.getAttribute("data-color-mode"));
  const setColorMode = (mode) => {
    const next = normalizeColorMode(mode);
    document.documentElement.setAttribute("data-color-mode", next);
    try {
      if (next === "system") localStorage.removeItem(COLOR_MODE_STORAGE_KEY);
      else localStorage.setItem(COLOR_MODE_STORAGE_KEY, next);
    } catch {
      // Ignore storage failures (private browsing, blocked storage, etc.)
    }
  };
  qsa("[data-color-mode-toggle]").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      const current = getColorMode();
      let next = "dark";
      if (current === "system") next = prefersDarkScheme() ? "light" : "dark";
      else if (current === "dark") next = "light";
      else if (current === "light") next = "system";
      setColorMode(next);
    });
  });

  const setAriaExpanded = (el, expanded) => {
    if (!el) return;
    el.setAttribute("aria-expanded", expanded ? "true" : "false");
  };

  const trapFocus = (container) => {
    const selector =
      'a[href]:not([aria-disabled="true"]), button:not([disabled]):not([aria-disabled="true"]), input:not([disabled]):not([aria-disabled="true"]), select:not([disabled]):not([aria-disabled="true"]), textarea:not([disabled]):not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"]):not([aria-disabled="true"])';
    const getFocusable = () =>
      qsa(selector, container).filter((el) => {
        const cs = window.getComputedStyle(el);
        if (cs.display === "none" || cs.visibility === "hidden") return false;
        return true;
      });

    const focusFirst = () => {
      const focusables = getFocusable();
      const preferred = qs("[data-autofocus], [data-overlay-close]", container) || focusables[0] || null;
      preferred?.focus?.();
    };

    const handleKeydown = (event) => {
      if (event.key !== "Tab") return;
      const focusables = getFocusable();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocusIn = (event) => {
      // If focus is moved outside the open container, force it back in.
      if (!container.classList.contains("is-open")) return;
      const t = event.target;
      if (t && container.contains(t)) return;
      focusFirst();
    };

    container.addEventListener("keydown", handleKeydown);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      container.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("focusin", handleFocusIn);
    };
  };

  // Overlay targets rendered inside section bands can be trapped under the fixed header
  // due to stacking contexts (e.g., `isolation: isolate`). Hoist non-header overlays to
  // a body-level root so z-index works consistently across templates.
  const ensureOverlayRoot = () => {
    const existing = document.getElementById("PmaOverlayRoot");
    if (existing) return existing;
    const root = document.createElement("div");
    root.id = "PmaOverlayRoot";
    root.setAttribute("data-pma-overlay-root", "");
    document.body.appendChild(root);
    return root;
  };

  const hoistOverlayBundle = (target, backdrops = []) => {
    try {
      if (!target) return;
      const isOverlayLike =
        target.classList?.contains("pma-drawer") || target.classList?.contains("pma-modal");
      if (!isOverlayLike) return;

      const root = ensureOverlayRoot();
      backdrops.forEach((backdrop) => {
        if (backdrop && backdrop.parentElement !== root) root.appendChild(backdrop);
      });
      if (target.parentElement !== root) root.appendChild(target);
    } catch {
      // Best-effort only: don't break core runtime if DOM mutation fails.
    }
  };

  const getOverlayBackdrops = (targetId) =>
    targetId
      ? qsa("[data-overlay-backdrop]").filter(
          (backdrop) => backdrop.getAttribute("data-target") === targetId,
        )
      : [];

  const ensureOverlayBackdrop = (targetId, target, toggle) => {
    if (!targetId || !target?.classList?.contains("pma-drawer")) return [];

    const existing = getOverlayBackdrops(targetId);
    if (existing.length) return existing;

    const backdrop = document.createElement("button");
    backdrop.className = "pma-drawer-backdrop";
    backdrop.type = "button";
    backdrop.hidden = true;
    backdrop.setAttribute("data-overlay-backdrop", "");
    backdrop.setAttribute("data-generated-overlay-backdrop", "true");
    backdrop.setAttribute("data-target", targetId);
    backdrop.setAttribute("tabindex", "-1");
    backdrop.setAttribute(
      "aria-label",
      toggle.getAttribute("data-close-label") ||
        (target.classList.contains("pma-filters-drawer") ? "Close filters" : "Close drawer"),
    );

    const parent = target.parentElement || ensureOverlayRoot();
    parent.insertBefore(backdrop, target);
    return [backdrop];
  };

  const setupToggle = (toggle) => {
    const targetId = toggle.getAttribute("data-target");
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;
    const isNavDrawer = target.classList.contains("pma-nav-drawer");

    const backdrops = ensureOverlayBackdrop(targetId, target, toggle);
    hoistOverlayBundle(target, backdrops);

    if (!target.hasAttribute("aria-hidden")) {
      target.setAttribute("aria-hidden", "true");
    }

    const openLabel = toggle.getAttribute("aria-label") || "Open menu";
    const closeLabel = toggle.getAttribute("data-close-label") || "Close menu";
    let releaseFocus = () => {};
    const open = () => {
      toggleClass(target, "is-open", true);
      backdrops.forEach((backdrop) => {
        backdrop.hidden = false;
        backdrop.setAttribute("aria-hidden", "false");
        toggleClass(backdrop, "is-open", true);
      });
      lockOverlay();
      if (isNavDrawer) {
        document.documentElement.classList.add("is-nav-open");
        document.body.classList.add("is-nav-open");
      }
      setAriaExpanded(toggle, true);
      toggle.setAttribute("aria-label", closeLabel);
      target.setAttribute("aria-hidden", "false");
      releaseFocus = trapFocus(target);
      const focusTarget = qs("[data-autofocus]", target) || qs("[data-overlay-close]", target) || target;
      const focusOverlayTarget = () => {
        try {
          focusTarget?.focus?.({ preventScroll: true });
        } catch {
          focusTarget?.focus?.();
        }
      };
      focusOverlayTarget();
      window.requestAnimationFrame?.(focusOverlayTarget);
      window.setTimeout(focusOverlayTarget, 160);
    };

    const close = ({ restoreFocus = true } = {}) => {
      toggleClass(target, "is-open", false);
      backdrops.forEach((backdrop) => {
        toggleClass(backdrop, "is-open", false);
        backdrop.setAttribute("aria-hidden", "true");
        backdrop.hidden = true;
      });
      unlockOverlay();
      if (isNavDrawer) {
        document.documentElement.classList.remove("is-nav-open");
        document.body.classList.remove("is-nav-open");
      }
      setAriaExpanded(toggle, false);
      toggle.setAttribute("aria-label", openLabel);
      target.setAttribute("aria-hidden", "true");
      releaseFocus();
      if (restoreFocus) {
        try {
          toggle.focus({ preventScroll: true });
        } catch {
          toggle.focus();
        }
      }
    };

    toggle.addEventListener("click", (event) => {
      event.preventDefault();
      const isOpen = target.classList.contains("is-open");
      if (isOpen) {
        close();
      } else {
        open();
      }
    });

    qsa("[data-overlay-close]", target).forEach((btn) => {
      btn.addEventListener("click", (event) => {
        event.preventDefault();
        close();
      });
    });

    backdrops.forEach((backdrop) => {
      backdrop.addEventListener("click", (event) => {
        event.preventDefault();
        close();
      });
    });

    qsa('a[href]', target).forEach((link) => {
      link.addEventListener("click", (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const linkTarget = String(link.getAttribute("target") || "").trim().toLowerCase();
        if (linkTarget && linkTarget !== "_self") return;
        const href = String(link.getAttribute("href") || "").trim();
        if (!href || href.startsWith("#")) return;
        close({ restoreFocus: false });
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      if (!target.classList.contains("is-open")) return;
      close();
    });
  };

  qsa("[data-nav-toggle]").forEach(setupToggle);
  qsa("[data-disclosure-toggle]").forEach(setupToggle);

  /* ── Header dropdown enhancements (details/summary) ─────────────
   * Close on Escape, close on outside click, single-open behavior.
   * The <details>/<summary> element works without JS; this layer is
   * progressive enhancement only.
   * ────────────────────────────────────────────────────────────── */
  const slugifyNavId = (value) =>
    String(value || "nav")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const ensureActiveMemberCategoriesLink = () => {
    if (!document.body.classList.contains("is-member-active")) return;
    const nav = qs(".pma-header__nav");
    if (!nav) return;
    if (nav.getAttribute("data-pma-inject-categories") === "false") return;
    if (window.matchMedia("(min-width: 768px)").matches) {
      nav.querySelectorAll(':scope > a[href*="/pages/faq"]').forEach((anchor) => anchor.remove());
    }
    if (nav.querySelector("[data-pma-nav-categories-fallback], [data-pma-nav-categories-enhanced]")) {
      return;
    }
    const visibleTopLabels = () => {
      const labels = [];
      const topNodes = nav.querySelectorAll(
        ":scope > a, :scope > .pma-nav-dropdown .pma-nav-dropdown__top-link, :scope > .pma-nav-dropdown--split .pma-nav-dropdown__top-link",
      );
      topNodes.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;
        const label = (el.textContent || "").trim().toLowerCase();
        if (label) labels.push(label);
      });
      return labels;
    };
    const hasCategories = visibleTopLabels().some(
      (label) => label.includes("categor") || label === "all products",
    );
    if (hasCategories) return;

    const href = nav.getAttribute("data-pma-categories-url") || "/collections/all";
    const text =
      nav.getAttribute("data-pma-categories-label") || "Categories";
    const link = document.createElement("a");
    link.href = href;
    link.textContent = text;
    link.setAttribute("data-pma-nav-area", "primary");
    link.setAttribute("data-pma-nav-categories-enhanced", "true");
    if (window.location.pathname === href.replace(/^https?:\/\/[^/]+/, "")) {
      link.classList.add("is-active");
    }

    const producers =
      nav.querySelector(':scope > a[href*="/pages/producers"]') ||
      [...nav.querySelectorAll(":scope > a")].find((el) =>
        (el.textContent || "").trim().toLowerCase().includes("producer"),
      );
    const howItWorks =
      nav.querySelector(':scope > a[href*="/pages/how-it-works"]') ||
      nav.querySelector(".pma-nav-dropdown--split, .pma-nav-dropdown");
    if (producers?.nextSibling) {
      producers.parentNode.insertBefore(link, producers.nextSibling);
    } else if (howItWorks) {
      nav.insertBefore(link, howItWorks);
    } else {
      nav.appendChild(link);
    }
  };

  const headerDropdowns = qsa("details.pma-nav-dropdown__menu");
  const closeAllHeaderDropdowns = (except) => {
    headerDropdowns.forEach((d) => {
      if (d.open && d !== except) d.removeAttribute("open");
    });
  };

  headerDropdowns.forEach((d, index) => {
    const summary = qs("summary", d);
    const panel = qs(".pma-nav-dropdown__panel", d);
    if (panel && !panel.id) {
      const labelSource =
        summary?.getAttribute("aria-label") ||
        summary?.textContent ||
        `menu-${index + 1}`;
      const labelCore = String(labelSource)
        .replace(/^open\s+/i, "")
        .replace(/\s+menu$/i, "");
      panel.id = `pmanavdropdownpanel-${slugifyNavId(labelCore)}-primary`;
    }
    if (summary && panel && panel.id && !summary.getAttribute("aria-controls")) {
      summary.setAttribute("aria-controls", panel.id);
    }
    if (summary && !summary.getAttribute("aria-expanded")) {
      summary.setAttribute("aria-expanded", d.open ? "true" : "false");
    }
    const syncExpanded = () => {
      if (summary) summary.setAttribute("aria-expanded", d.open ? "true" : "false");
    };
    syncExpanded();
    d.addEventListener("toggle", () => {
      syncExpanded();
      if (!d.open) return;
      closeAllHeaderDropdowns(d);
      syncExpanded();
    });
  });

  ensureActiveMemberCategoriesLink();

  /* GH-6752: progressive header collapse when zoom/resolution squeezes the primary row. */
  const bindHeaderResponsiveFitment = () => {
    const header = qs(".pma-header");
    const inner = qs(".pma-header__primary-row") || qs(".pma-header__inner");
    if (!header || !inner) return;

    const FITMENT_CLASSES = [
      "is-header-fitment-tight",
      "is-header-fitment-compact",
      "is-header-fitment-critical",
    ];

    const clearFitment = () => {
      FITMENT_CLASSES.forEach((name) => header.classList.remove(name));
    };

    const isDesktopHeader = () =>
      Boolean(window.matchMedia?.("(min-width: 1100px)")?.matches);

    const measureCollision = () => {
      const lead = qs(".pma-header__lead", header);
      const nav = qs(".pma-header__nav", header);
      const actions = qs(".pma-header__actions", header);
      if (!lead || !nav || !actions) return false;

      const leadRect = lead.getBoundingClientRect();
      const navRect = nav.getBoundingClientRect();
      const actionsRect = actions.getBoundingClientRect();
      if (navRect.width <= 0 || actionsRect.width <= 0) return false;

      const navOverlapsLead = navRect.left < leadRect.right - 2;
      const navOverlapsActions = navRect.right > actionsRect.left + 2;
      const actionsOverlapLead = actionsRect.left < leadRect.right - 2;
      return navOverlapsLead || navOverlapsActions || actionsOverlapLead;
    };

    const rowOverflows = () => inner.scrollWidth > inner.clientWidth + 2;

    const applyFitment = () => {
      if (!isDesktopHeader()) {
        clearFitment();
        return;
      }

      clearFitment();
      if (!rowOverflows() && !measureCollision()) return;

      header.classList.add("is-header-fitment-tight");
      if (rowOverflows() || measureCollision()) {
        header.classList.add("is-header-fitment-compact");
      }
      if (rowOverflows() || measureCollision()) {
        header.classList.add("is-header-fitment-critical");
      }
    };

    let frame = 0;
    const schedule = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        applyFitment();
      });
    };

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(schedule);
      observer.observe(inner);
      const surface = qs(".pma-header__surface", header);
      if (surface) observer.observe(surface);
    }

    window.addEventListener("resize", schedule, { passive: true });
    schedule();
  };

  bindHeaderResponsiveFitment();

  const supportsFineHover = window.matchMedia?.("(hover: hover) and (pointer: fine)");
  qsa(".pma-nav-dropdown--split").forEach((dropdown) => {
    const details = qs("details.pma-nav-dropdown__menu", dropdown);
    if (!details) return;
    const panel = qs(".pma-nav-dropdown__panel", details);

    let openTimer = 0;
    let closeTimer = 0;
    const clearTimers = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };
    const openDropdown = () => {
      clearTimers();
      details.open = true;
      closeAllHeaderDropdowns(details);
    };
    const openFromHover = () => {
      if (supportsFineHover && !supportsFineHover.matches) return;
      clearTimers();
      openTimer = window.setTimeout(openDropdown, 80);
    };
    const closeFromHover = () => {
      if (supportsFineHover && !supportsFineHover.matches) return;
      clearTimers();
      closeTimer = window.setTimeout(() => details.removeAttribute("open"), 180);
    };

    const cancelCloseFromHover = () => {
      window.clearTimeout(closeTimer);
    };

    dropdown.addEventListener("mouseenter", openFromHover);
    dropdown.addEventListener("mouseleave", closeFromHover);
    if (panel) {
      panel.addEventListener("mouseenter", cancelCloseFromHover);
      panel.addEventListener("mouseleave", closeFromHover);
    }
    dropdown.addEventListener("focusin", openDropdown);
    dropdown.addEventListener("focusout", () => {
      clearTimers();
      closeTimer = window.setTimeout(() => {
        if (!dropdown.contains(document.activeElement)) details.removeAttribute("open");
      }, 180);
    });
  });

  // Close on Escape (and return focus to the first open summary).
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const focused = document.activeElement;
    const ownerDropdown = focused
      ? focused.closest("details.pma-nav-dropdown__menu")
      : null;
    const summaryToFocus = ownerDropdown
      ? qs("summary", ownerDropdown)
      : null;
    closeAllHeaderDropdowns();
    if (summaryToFocus) summaryToFocus.focus();
  });

  // Close on click outside.
  document.addEventListener("click", (e) => {
    if (e.target.closest("details.pma-nav-dropdown__menu")) return;
    closeAllHeaderDropdowns();
  });

  const accordionToggleByTarget = new Map();
  const accordionToggleByItemId = new Map();
  const accordionToggleByTriggerId = new Map();
  const getHashId = () => {
    const hash = window.location.hash || "";
    return hash ? hash.slice(1) : "";
  };
  const openAccordionByHash = (hashId, { focus = false } = {}) => {
    if (!hashId) return false;
    const toggle =
      accordionToggleByItemId.get(hashId) ||
      accordionToggleByTriggerId.get(hashId) ||
      accordionToggleByTarget.get(hashId) ||
      null;
    if (!toggle || typeof toggle.__pmaAccordionOpen !== "function") return false;
    if (toggle.getAttribute("aria-expanded") !== "true") {
      toggle.__pmaAccordionOpen();
    }
    if (focus && typeof toggle.focus === "function") {
      try {
        toggle.focus({ preventScroll: true });
      } catch {
        // Older browsers can reject the focus options object; retry without it.
        toggle.focus();
      }
    }
    return true;
  };

  const setupAccordionToggle = (toggle) => {
    const targetId = toggle.getAttribute("data-target");
    const panel = targetId ? document.getElementById(targetId) : null;
    if (!panel) return;

    const item = toggle.closest?.(".pma-accordion__item");

    const open = () => {
      panel.hidden = false;
      toggleClass(item, "is-open", true);
      setAriaExpanded(toggle, true);
    };

    const close = () => {
      panel.hidden = true;
      toggleClass(item, "is-open", false);
      setAriaExpanded(toggle, false);
    };

    // Normalize initial state from the DOM (Liquid sets `hidden` initially).
    if (!panel.hidden) open();
    else close();

    toggle.__pmaAccordionOpen = open;
    accordionToggleByTarget.set(targetId, toggle);
    if (item?.id) accordionToggleByItemId.set(item.id, toggle);
    if (toggle.id) accordionToggleByTriggerId.set(toggle.id, toggle);

    toggle.addEventListener("click", (event) => {
      event.preventDefault();
      if (panel.hidden) open();
      else close();
    });
  };

  qsa("[data-accordion-toggle]").forEach(setupAccordionToggle);

  /*
   * PDP Product Information:
   *
   * Desktop uses the left rail as an exclusive content selector.
   * Mobile/tablet retain the normal accordion interaction.
   *
   * This deliberately does not scroll when a rail item is selected.
   */
  const setupPdpDetailsSwitcher = (nav) => {
    const section = nav.closest?.(
      ".pma-section--commerce-product-details-blocks",
    );
    if (!section) return null;

    const desktop = window.matchMedia("(min-width: 1024px)");
    const links = qsa('a[href^="#PmaDetailPanel-"]', nav);
    const panels = qsa(".pma-detail-panel", section);

    if (!links.length || !panels.length) return null;

    const panelById = new Map(
      panels
        .filter((panel) => panel.id)
        .map((panel) => [panel.id, panel]),
    );

    const contains = (panelId) =>
      Boolean(panelId && panelById.has(panelId));

    const activate = (
      panelId,
      { updateHash = false } = {},
    ) => {
      const target = panelById.get(panelId);
      if (!target) return false;

      panels.forEach((panel) => {
        const active = panel === target;
        const trigger = qs("[data-accordion-toggle]", panel);
        const contentId = trigger?.getAttribute("data-target") || "";
        const content = contentId
          ? document.getElementById(contentId)
          : null;

        toggleClass(panel, "is-open", active);
        setAriaExpanded(trigger, active);

        if (content) {
          content.hidden = !active;
        }
      });

      links.forEach((link) => {
        const href = link.getAttribute("href") || "";
        const id = href.startsWith("#") ? href.slice(1) : "";
        const active = id === panelId;

        toggleClass(link, "is-active", active);

        if (active) {
          link.setAttribute("aria-current", "page");
        } else {
          link.removeAttribute("aria-current");
        }
      });

      if (updateHash && window.history?.replaceState) {
        window.history.replaceState(
          null,
          "",
          "#" + panelId,
        );
      }

      return true;
    };

    const syncDesktopMode = () => {
      if (!desktop.matches) return;

      const hashId = getHashId();
      const initiallyOpen =
        panels.find((panel) =>
          panel.classList.contains("is-open"),
        )?.id || panels[0]?.id || "";

      activate(
        contains(hashId) ? hashId : initiallyOpen,
      );
    };

    nav.addEventListener("click", (event) => {
      if (!desktop.matches) return;

      const link = event.target?.closest(
        'a[href^="#PmaDetailPanel-"]',
      );
      if (!link) return;

      const href = link.getAttribute("href") || "";
      const panelId =
        href.startsWith("#") ? href.slice(1) : "";

      if (!contains(panelId)) return;

      event.preventDefault();
      activate(panelId, { updateHash: true });
    });

    /*
     * Desktop panel headers are visually hidden, but intercept them
     * defensively so another script cannot create multiple open panels.
     */
    section.addEventListener(
      "click",
      (event) => {
        if (!desktop.matches) return;

        const trigger = event.target?.closest(
          "[data-accordion-toggle]",
        );
        if (!trigger || !section.contains(trigger)) return;

        const panel = trigger.closest(
          ".pma-detail-panel",
        );
        if (!panel?.id) return;

        event.preventDefault();
        event.stopPropagation();

        activate(panel.id, { updateHash: true });
      },
      true,
    );

    if (desktop.addEventListener) {
      desktop.addEventListener(
        "change",
        syncDesktopMode,
      );
    } else if (desktop.addListener) {
      desktop.addListener(syncDesktopMode);
    }

    syncDesktopMode();

    return {
      contains,
      activate,
      isDesktop: () => desktop.matches,
    };
  };

  const pdpDetailsSwitchers = qsa(
    ".pma-details-nav",
  )
    .map(setupPdpDetailsSwitcher)
    .filter(Boolean);

  const openPdpDetailsByHash = (hashId) => {
    if (!hashId) return false;

    for (const switcher of pdpDetailsSwitchers) {
      if (
        switcher.isDesktop() &&
        switcher.contains(hashId)
      ) {
        return switcher.activate(hashId);
      }
    }

    return false;
  };

  const initialAccordionHash = getHashId();

  if (!openPdpDetailsByHash(initialAccordionHash)) {
    openAccordionByHash(initialAccordionHash);
  }

  window.addEventListener("hashchange", () => {
    const hashId = getHashId();

    if (!openPdpDetailsByHash(hashId)) {
      openAccordionByHash(hashId, {
        focus: true,
      });
    }
  });

  const setupTabs = (tabs) => {
    const root = tabs.closest?.("[data-section-id]") || tabs.parentElement;
    if (!root) return;

    const links = qsa("[data-pma-tab]", tabs);
    const panels = qsa("[data-pma-tab-panel]", root);
    if (links.length < 2 || panels.length < 2) return;

    const panelById = new Map();
    panels.forEach((panel) => {
      if (!panel.id) return;
      panelById.set(panel.id, panel);
    });

    const firstId = links[0]?.getAttribute("aria-controls") || "";

    const getIdFromHash = () => {
      const hash = window.location.hash || "";
      if (!hash.startsWith("#")) return "";
      return hash.slice(1);
    };

    const setActive = (panelId) => {
      const targetId = panelById.has(panelId) ? panelId : firstId;
      if (!targetId) return;

      links.forEach((link) => {
        const controls = link.getAttribute("aria-controls");
        const isActive = controls === targetId;
        toggleClass(link, "is-active", isActive);
        // aria-selected is valid only on role="tab"; ensure role before setting (OPP-023)
        if (link.getAttribute("role") !== "tab") link.setAttribute("role", "tab");
        link.setAttribute("aria-selected", isActive ? "true" : "false");
        link.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      panels.forEach((panel) => {
        panel.hidden = panel.id !== targetId;
      });
    };

    setActive(getIdFromHash() || firstId);

    links.forEach((link) => {
      link.addEventListener("click", (event) => {
        const targetId = link.getAttribute("aria-controls");
        if (!targetId) return;
        if (!panelById.has(targetId)) return;
        event.preventDefault();
        setActive(targetId);
        try {
          window.history.replaceState(null, "", `#${targetId}`);
        } catch {
          // Fallback for older browsers.
          window.location.hash = targetId;
        }
      });
    });

    window.addEventListener("hashchange", () => {
      setActive(getIdFromHash());
    });
  };

  qsa("[data-pma-tabs]").forEach(setupTabs);

  const getTemplateRoot = (templateEl) => templateEl?.content || templateEl;

  const getImageHiResUrl = (img) => {
    if (!img) return "";
    const srcset = img.getAttribute("srcset");
    if (srcset) {
      const parts = srcset
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
      const last = parts[parts.length - 1];
      const candidate = last?.split(/\s+/)[0];
      if (candidate) return candidate;
    }
    return img.currentSrc || img.getAttribute("src") || img.src || "";
  };

  let galleryLightboxState = null;

  const ensureGalleryLightbox = () => {
    if (galleryLightboxState) return galleryLightboxState;

    const root = ensureOverlayRoot();
    const backdrop = document.createElement("button");
    backdrop.type = "button";
    backdrop.className = "pma-overlay pma-gallery-lightbox__backdrop";
    backdrop.setAttribute("aria-label", "Close enlarged image");
    backdrop.hidden = true;
    backdrop.setAttribute("aria-hidden", "true");

    const modal = document.createElement("div");
    modal.id = "PmaGalleryLightbox";
    modal.className = "pma-modal pma-gallery-lightbox";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("aria-label", "Enlarged product image");
    modal.innerHTML =
      '<div class="pma-gallery-lightbox__frame">' +
      '<button type="button" class="pma-gallery-lightbox__close" data-overlay-close aria-label="Close enlarged image"><span aria-hidden="true">&times;</span></button>' +
      '<button type="button" class="pma-gallery-lightbox__nav pma-gallery-lightbox__nav--prev" data-pma-gallery-lightbox-prev aria-label="Previous image"><span aria-hidden="true">&lsaquo;</span></button>' +
      '<figure class="pma-gallery-lightbox__figure"><img class="pma-gallery-lightbox__img" alt="" decoding="async" /></figure>' +
      '<button type="button" class="pma-gallery-lightbox__nav pma-gallery-lightbox__nav--next" data-pma-gallery-lightbox-next aria-label="Next image"><span aria-hidden="true">&rsaquo;</span></button>' +
      "</div>";

    root.appendChild(backdrop);
    root.appendChild(modal);

    const imgEl = qs(".pma-gallery-lightbox__img", modal);
    const closeBtn = qs("[data-overlay-close]", modal);
    const prevBtn = qs("[data-pma-gallery-lightbox-prev]", modal);
    const nextBtn = qs("[data-pma-gallery-lightbox-next]", modal);

    let releaseFocus = () => {};
    let lastTrigger = null;
    let items = [];
    let index = 0;
    let onIndexChange = null;

    const render = () => {
      const item = items[index];
      if (!item) return;
      imgEl.src = item.url;
      imgEl.alt = item.alt || "";
      const hasMultiple = items.length > 1;
      prevBtn.hidden = !hasMultiple;
      nextBtn.hidden = !hasMultiple;
      prevBtn.disabled = index <= 0;
      nextBtn.disabled = index >= items.length - 1;
      modal.setAttribute(
        "aria-label",
        item.alt ? `Enlarged image: ${item.alt}` : "Enlarged product image",
      );
    };

    const close = () => {
      toggleClass(modal, "is-open", false);
      toggleClass(backdrop, "is-open", false);
      backdrop.hidden = true;
      backdrop.setAttribute("aria-hidden", "true");
      modal.setAttribute("aria-hidden", "true");
      unlockOverlay();
      releaseFocus();
      imgEl.removeAttribute("src");
      if (lastTrigger?.focus) lastTrigger.focus();
      lastTrigger = null;
      onIndexChange = null;
    };

    const open = ({ items: nextItems, startIndex = 0, trigger, onChange }) => {
      if (!nextItems?.length) return;
      items = nextItems;
      index = Math.max(0, Math.min(startIndex, items.length - 1));
      onIndexChange = typeof onChange === "function" ? onChange : null;
      lastTrigger = trigger || null;
      render();
      toggleClass(modal, "is-open", true);
      toggleClass(backdrop, "is-open", true);
      backdrop.hidden = false;
      backdrop.setAttribute("aria-hidden", "false");
      modal.setAttribute("aria-hidden", "false");
      lockOverlay();
      releaseFocus = trapFocus(modal);
      closeBtn.focus();
    };

    const step = (delta) => {
      const next = index + delta;
      if (next < 0 || next >= items.length) return;
      index = next;
      render();
      onIndexChange?.(items[index]?.mediaId);
    };

    closeBtn.addEventListener("click", (event) => {
      event.preventDefault();
      close();
    });
    backdrop.addEventListener("click", (event) => {
      event.preventDefault();
      close();
    });

    // PDP lightbox click-away close:
    // the full-screen modal sits above its sibling backdrop, so clicks on
    // modal whitespace never reliably reach the backdrop. Any non-control
    // click inside this image-only modal should dismiss it.
    modal.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("button")) return;

      event.preventDefault();
      close();
    });
    prevBtn.addEventListener("click", (event) => {
      event.preventDefault();
      step(-1);
    });
    nextBtn.addEventListener("click", (event) => {
      event.preventDefault();
      step(1);
    });

    document.addEventListener("keydown", (event) => {
      if (!modal.classList.contains("is-open")) return;
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        step(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        step(1);
      }
    });

    galleryLightboxState = { open, close, modal };
    return galleryLightboxState;
  };

  const setupGallery = (gallery) => {
    const main = qs("[data-pma-gallery-main]", gallery);
    const thumbs = qsa("[data-pma-gallery-thumb]", gallery);
    const sources = qsa("[data-pma-gallery-source]", gallery);

    if (!main || !thumbs.length || !sources.length) return;

    const sourceById = new Map();
    sources.forEach((el) => {
      const mediaId = el.getAttribute("data-media-id");
      if (!mediaId) return;
      sourceById.set(mediaId, el);
    });

    const getActiveMediaId = () => {
      const active = thumbs.find((btn) => btn.classList.contains("is-active"));
      return active?.getAttribute("data-media-id") || thumbs[0]?.getAttribute("data-media-id") || "";
    };

    const getLightboxItems = () => {
      const items = [];
      thumbs.forEach((btn) => {
        const mediaId = btn.getAttribute("data-media-id");
        const mediaType = btn.getAttribute("data-media-type") || "image";
        if (!mediaId || mediaType !== "image") return;
        const source = sourceById.get(mediaId);
        const img = source ? qs("img", getTemplateRoot(source)) : null;
        const url = getImageHiResUrl(img);
        if (!url) return;
        items.push({ mediaId, url, alt: img?.getAttribute("alt") || "" });
      });
      return items;
    };

    const bindMainImageZoom = () => {
      if (gallery.getAttribute("data-zoom-enabled") !== "true") return;
      const img = qs("img", main);
      if (!img) return;

      const zoomLabel = img.alt ? `View larger image: ${img.alt}` : "View larger image";
      img.setAttribute("role", "button");
      img.setAttribute("tabindex", "0");
      img.setAttribute("aria-label", zoomLabel);

      const openZoom = () => {
        const zoomMode = gallery.getAttribute("data-zoom-mode") || "lightbox";
        const url = getImageHiResUrl(img);
        if (!url) return;
        if (zoomMode === "new_tab") {
          window.open(url, "_blank", "noopener");
          return;
        }
        const lightbox = ensureGalleryLightbox();
        const items = getLightboxItems();
        if (!items.length) return;
        const activeId = getActiveMediaId();
        const startIndex = Math.max(0, items.findIndex((item) => item.mediaId === activeId));
        lightbox.open({
          items,
          startIndex,
          trigger: img,
          onChange: (mediaId) => {
            if (mediaId) setActive(mediaId);
          },
        });
      };

      img.addEventListener("click", openZoom);
      img.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openZoom();
      });
    };

    const setActive = (mediaId) => {
      const source = sourceById.get(mediaId);
      if (!source) return;

      main.innerHTML = source.innerHTML;

      thumbs.forEach((btn) => {
        const isActive = btn.getAttribute("data-media-id") === mediaId;
        toggleClass(btn, "is-active", isActive);
        btn.setAttribute("aria-current", isActive ? "true" : "false");
      });

      bindMainImageZoom();
    };

    bindMainImageZoom();

    thumbs.forEach((btn) => {
      btn.addEventListener("click", () => {
        const mediaId = btn.getAttribute("data-media-id");
        if (!mediaId) return;
        setActive(mediaId);
      });
      btn.addEventListener("keydown", (event) => {
        const currentIndex = thumbs.indexOf(btn);
        let nextIndex = currentIndex;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = Math.min(currentIndex + 1, thumbs.length - 1);
        else if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = Math.max(currentIndex - 1, 0);
        else if (event.key === "Home") nextIndex = 0;
        else if (event.key === "End") nextIndex = thumbs.length - 1;
        else return;
        event.preventDefault();
        const nextThumb = thumbs[nextIndex];
        const mediaId = nextThumb?.getAttribute("data-media-id");
        if (!mediaId) return;
        setActive(mediaId);
        nextThumb.focus();
      });
    });
  };

  qsa("[data-pma-gallery]").forEach(setupGallery);

  if (prefersReducedMotion?.matches) {
    document.documentElement.classList.add("prefers-reduced-motion");
  }

  /* Header sticky border (page_specs §2.1): add .is-stuck when scrolled */
  const header = qs(".pma-header");
  if (header) {
    const updateStuck = () => {
      toggleClass(header, "is-stuck", window.scrollY > 0);
    };
    updateStuck();
    window.addEventListener("scroll", updateStuck, { passive: true });
  }

  /* Announcement rail: collapsible (not dismissible) + persists across sessions; resets on content change. */
  const ANNOUNCEMENT_STORAGE_KEY = "pma_announcement_rail_state_v1";
  // Collapsed height must never be smaller than the toggle control.
  const ANNOUNCEMENT_COLLAPSED_DESKTOP = "40px";
  const ANNOUNCEMENT_COLLAPSED_MOBILE = "44px";

  const hashFNV1a = (input) => {
    const str = String(input || "");
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i += 1) {
      h ^= str.charCodeAt(i);
      // FNV-1a: h *= 16777619 (implemented via shifts to stay in 32-bit)
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    // unsigned 32-bit hex
    return (h >>> 0).toString(16);
  };

  const loadAnnouncementState = () => {
    try {
      const raw = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const saveAnnouncementState = (next) => {
    try {
      localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(next || {}));
    } catch {
      // ignore storage failures
    }
  };

  const setAnnouncementCollapsedVars = (collapsed) => {
    try {
      if (!collapsed) {
        document.documentElement.style.removeProperty("--pma-announcement-rail-h-desktop");
        document.documentElement.style.removeProperty("--pma-announcement-rail-h-mobile");
        return;
      }
      document.documentElement.style.setProperty(
        "--pma-announcement-rail-h-desktop",
        ANNOUNCEMENT_COLLAPSED_DESKTOP,
      );
      document.documentElement.style.setProperty(
        "--pma-announcement-rail-h-mobile",
        ANNOUNCEMENT_COLLAPSED_MOBILE,
      );
    } catch {
      // ignore
    }
  };

  const initAnnouncementRailCollapse = () => {
    const section = qs('[data-section-handle="pma__global__announcement_bar"][data-pma-announcement-sig]');
    if (!section) return;
    const toggleBtn = qs("[data-pma-announcement-toggle]", section);
    const content = qs(".pma-announcement-rail__content", section);
    if (!toggleBtn || !content) return;

    const sig = section.getAttribute("data-pma-announcement-sig") || "";
    const sigHash = hashFNV1a(sig);

    const stored = loadAnnouncementState();
    const contentMatches = Boolean(stored && stored.sigHash && stored.sigHash === sigHash);
    const shouldCollapse = Boolean(contentMatches && stored.collapsed === true);

    if (!contentMatches) {
      saveAnnouncementState({ sigHash, collapsed: false, updatedAt: Date.now() });
    }

    const apply = (collapsed) => {
      toggleClass(section, "is-collapsed", collapsed);
      setAnnouncementCollapsedVars(collapsed);
      toggleBtn.setAttribute("aria-expanded", collapsed ? "false" : "true");
      toggleBtn.setAttribute(
        "aria-label",
        collapsed ? "Expand announcements" : "Collapse announcements",
      );
      if (collapsed) {
        // Ensure any expanded multi-banner panel is closed while collapsed.
        const details = qs(".pma-announcement-rail__details", section);
        if (details) details.open = false;
      }
    };

    apply(shouldCollapse);

    toggleBtn.addEventListener("click", () => {
      const nextCollapsed = !section.classList.contains("is-collapsed");
      apply(nextCollapsed);
      saveAnnouncementState({ sigHash, collapsed: nextCollapsed, updatedAt: Date.now() });
    });
  };

  initAnnouncementRailCollapse();

  /* PRD-OPP-0030: Join plans form — sync hidden id with selected plan; loading state and double-submit prevention */
  /* F02.2 TCK-009 / PRD-OPP-0003 join-007 / PRD-OPP-0015: Join funnel analytics (no PII).
   * Taxonomy: pma_join_view (from analytics.core firePageView), pma_join_plan_select, pma_join_submit, pma_join_submit_error. */
  const joinForm = qs("[data-pma-join-form]");
  const trackJoin = (eventName, payload) => {
    if (window.PMA && window.PMA.analytics && typeof window.PMA.analytics.track === "function") {
      window.PMA.analytics.track(eventName, payload);
    }
    const detail = { surface: "join_page", ...payload };
    document.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
    if (typeof window.dataLayer !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...detail });
    }
  };

  document.addEventListener("pma:join:submit_error", function (e) {
    const d = e && e.detail;
    if (!d) return;
    trackJoin("pma_join_submit_error", {
      error_type: (d.error_type && String(d.error_type).slice(0, 64)) || "unknown",
      plan: d.plan || undefined,
      surface: d.surface || "join_page",
    });
  });

  if (joinForm) {
    const hiddenId = qs('input[name="id"]', joinForm);
    const planRadios = qsa('input[name="pma_membership_plan"]', joinForm);
    const submitBtns = qsa(".pma-join-plans__submit", joinForm);
    const errorSummary = qs("[data-pma-error-summary]", joinForm);
    const errorList = qs("[data-join-form-errors]", joinForm);
    const agreementCheckbox = joinForm.querySelector(
      'input[type="checkbox"][required][id^="Agreement-"]',
    );

    const hideJoinFormErrors = () => {
      if (agreementCheckbox) agreementCheckbox.removeAttribute("aria-invalid");
      if (errorList) errorList.innerHTML = "";
      if (!errorSummary) return;
      errorSummary.hidden = true;
      errorSummary.setAttribute("aria-hidden", "true");
    };

    const showJoinFormErrors = (items) => {
      if (items.length === 0) {
        hideJoinFormErrors();
        return;
      }
      if (errorList) {
        errorList.innerHTML = "";
        items.forEach(({ field, message }) => {
          const li = document.createElement("li");
          const link = document.createElement("a");
          const fieldId = field && field.id ? String(field.id) : "";
          link.href = fieldId ? `#${fieldId}` : "#";
          link.textContent = String(message || "This field is required.");
          link.addEventListener("click", (e) => {
            if (!field || typeof field.focus !== "function") return;
            e.preventDefault();
            try {
              field.focus({ preventScroll: false });
              field.scrollIntoView({ block: "center", behavior: "smooth" });
            } catch {
              field.focus();
            }
          });
          li.appendChild(link);
          errorList.appendChild(li);
        });
      }
      if (agreementCheckbox && !agreementCheckbox.checked) {
        agreementCheckbox.setAttribute("aria-invalid", "true");
      }
      if (!errorSummary) return;
      errorSummary.hidden = false;
      errorSummary.setAttribute("aria-hidden", "false");
      try {
        errorSummary.focus({ preventScroll: false });
        errorSummary.scrollIntoView({ block: "start", behavior: "smooth" });
      } catch {
        // ignore
      }
    };

    const getSelectedPlanTerm = () => {
      const checked = qs('input[name="pma_membership_plan"]:checked', joinForm);
      return checked ? checked.value : null;
    };

    const validateJoinForm = () => {
      const errors = [];
      if (agreementCheckbox && !agreementCheckbox.checked) {
        const label = agreementCheckbox.id
          ? qs(`label[for="${agreementCheckbox.id}"]`, joinForm)
          : null;
        const labelText = label && label.textContent
          ? label.textContent.replace(/\*/g, "").trim()
          : "Membership terms";
        errors.push({ field: agreementCheckbox, message: `${labelText} is required.` });
      }
      return errors;
    };

    submitBtns.forEach((btn) => {
      btn.addEventListener("click", (event) => {
        const errors = validateJoinForm();
        if (errors.length === 0) {
          hideJoinFormErrors();
          return;
        }
        event.preventDefault();
        showJoinFormErrors(errors);
        document.dispatchEvent(new CustomEvent("pma:join:submit_error", {
          detail: { error_type: "validation", plan: getSelectedPlanTerm() || undefined, surface: "join_page" },
          bubbles: true,
        }));
      });
    });

    agreementCheckbox?.addEventListener("change", () => {
      if (agreementCheckbox.checked) hideJoinFormErrors();
    });

    const setHiddenIdFromRadio = () => {
      const checked = qs('input[name="pma_membership_plan"]:checked', joinForm);
      if (hiddenId && checked && checked.dataset.variantId) {
        hiddenId.value = checked.dataset.variantId;
      }
      const sellingPlanInput = qs('input[name="selling_plan"]', joinForm);
      if (sellingPlanInput && checked) {
        const key =
          checked.value === "monthly"
            ? "monthlySellingPlanId"
            : checked.value === "supporter"
              ? "supporterSellingPlanId"
              : "annualSellingPlanId";
        sellingPlanInput.value = joinForm.dataset[key] || "";
      }
    };

    planRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        setHiddenIdFromRadio();
        const plan = radio.value;
        if (plan) {
          const sellingPlanInput = qs('input[name="selling_plan"]', joinForm);
          const planIdRaw = (sellingPlanInput && sellingPlanInput.value) ? sellingPlanInput.value : (radio.dataset.variantId || "");
          const label = radio.id ? qs('label[for="' + radio.id + '"]', joinForm) : null;
          const planName = (label && label.textContent) ? label.textContent.trim() : plan;
          trackJoin("pma_join_plan_select", {
            plan_id: String(planIdRaw || plan),
            plan_name: String(planName || plan).slice(0, 100),
            selection_mode: "radio",
            plan_term: plan,
          });
        }
      });
    });
    setHiddenIdFromRadio();

    const cleanPath = (href) => {
      if (!href) return undefined;
      try {
        const u = new URL(String(href), window.location.origin);
        return u.pathname;
      } catch {
        return String(href).split("?")[0].split("#")[0];
      }
    };

    joinForm.addEventListener("submit", (event) => {
      const errors = validateJoinForm();
      if (errors.length > 0) {
        event.preventDefault();
        showJoinFormErrors(errors);
        document.dispatchEvent(new CustomEvent("pma:join:submit_error", {
          detail: { error_type: "validation", plan: getSelectedPlanTerm() || undefined, surface: "join_page" },
          bubbles: true,
        }));
        return;
      }
      hideJoinFormErrors();
      const checked = qs('input[name="pma_membership_plan"]:checked', joinForm);
      const plan = checked ? checked.value : null;
      const sellingPlanInput = qs('input[name="selling_plan"]', joinForm);
      const planIdRaw = (sellingPlanInput && sellingPlanInput.value)
        ? sellingPlanInput.value
        : (checked && checked.dataset && checked.dataset.variantId ? checked.dataset.variantId : "");
      if (planIdRaw) {
        trackJoin("pma_membership_purchase_start", {
          plan_id: String(planIdRaw),
          checkout_context: "join_form_cart_add",
        });
      }
      const returnToInput = joinForm.querySelector('input[name="return_to"]');
      const returnTo = returnToInput ? returnToInput.value : undefined;
      trackJoin("pma_join_submit", {
        plan: plan || undefined,
        return_to: cleanPath(returnTo) || undefined,
        method: "js",
      });
      submitBtns.forEach((btn) => {
        btn.disabled = true;
        btn.setAttribute("aria-busy", "true");
        const orig = btn.textContent;
        btn.textContent = "Working…";
        btn.dataset.pmaOriginalLabel = orig;
      });
    });
  }

  // ── Product variant selector (PDP option buttons) ──
  qsa("[data-pma-variant-picker]").forEach((picker) => {
    const dataScript = qs("[data-pma-variant-data]", picker);
    const targetId = picker.getAttribute("data-variant-target");
    const hiddenId = targetId ? document.getElementById(targetId) : null;
    const priceEl = qs("[data-pma-variant-price]", picker);
    const form = picker.closest("form");
    const actionButtons = form ? qsa(".pma-atc, .pma-buy-now", form) : [];
    let variants = [];

    try {
      variants = JSON.parse(dataScript?.textContent || "[]");
    } catch {
      variants = [];
    }

    const selectedOptions = () =>
      qsa("[data-pma-variant-option]:checked", picker)
        .sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0))
        .map((input) => input.value);

    const findVariant = () => {
      const selected = selectedOptions();
      return variants.find((variant) => {
        const options = Array.isArray(variant.options) ? variant.options : [];
        return options.length === selected.length && options.every((option, index) => String(option) === String(selected[index]));
      });
    };

    const syncVariant = () => {
      const variant = findVariant();
      if (!variant) return;
      if (hiddenId && variant.id) hiddenId.value = String(variant.id);
      if (priceEl && variant.price) priceEl.textContent = variant.price;
      const unavailable = variant.available === false;
      actionButtons.forEach((button) => {
        button.disabled = unavailable;
        button.setAttribute("aria-disabled", unavailable ? "true" : "false");
      });
    };

    qsa("[data-pma-variant-option]", picker).forEach((input) => {
      input.addEventListener("change", syncVariant);
    });
    syncVariant();
  });

  // ── Product selling-plan selector (subscription / recurring reorder) ──
  // Binds to [data-pma-selling-plan-selector] containers rendered by
  // pma__commerce__selling_plan_selector snippet.
  // Phase 1: anchor day selector + schedule summary (spec § 7.1).
  qsa("[data-pma-selling-plan-selector]").forEach((container) => {
    const radios = qsa("[data-pma-sp-radio]", container);
    const freqWrap = qs("[data-pma-sp-frequencies]", container);
    const freqSelect = qs("[data-pma-sp-frequency-select]", container);
    const form = container.closest("form");
    let hiddenInput = qs("[data-pma-sp-hidden]", container);
    const options = qsa("[data-pma-sp-option]", container);
    const anchorWrap = qs("[data-pma-sp-anchor]", container);
    const anchorSelect = qs("[data-pma-sp-anchor-select]", container);
    const summaryEl = qs("[data-pma-sp-summary]", container);
    let anchorUserSet = false;

    if (!hiddenInput && form) {
      // The no-JS fallback owns its own selling_plan radios inside <noscript>.
      // When JS is enabled, create the hidden input for the enhanced selector path.
      hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = "selling_plan";
      hiddenInput.value = "";
      hiddenInput.setAttribute("data-pma-sp-hidden", "");
      form.appendChild(hiddenInput);
    }

    // Map of weekday values (1=Mon … 5=Fri)
    const weekdayNames = { 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday" };

    // Get selected plan name from frequency dropdown
    function getSelectedPlanName() {
      return freqSelect && freqSelect.selectedOptions[0]
        ? freqSelect.selectedOptions[0].textContent.trim()
        : "";
    }

    // Get ordinal suffix for a day number (1st, 2nd, 3rd, 4th…)
    function ordinalSuffix(n) {
      const s = String(n);
      if (s === "1" || s === "21" || s === "31") return "st";
      if (s === "2" || s === "22") return "nd";
      if (s === "3" || s === "23") return "rd";
      return "th";
    }

    // Detect frequency type from selling plan name
    function detectFrequencyType(planName) {
      if (!planName) return "weekly";
      const lower = planName.toLowerCase();
      if (lower.indexOf("month") !== -1) return "monthly";
      if (lower.indexOf("biweek") !== -1 || lower.indexOf("2 week") !== -1 || lower.indexOf("every 2") !== -1) return "biweekly";
      return "weekly";
    }

    function extractMonthlyAnchorFromPlanName(planName) {
      if (detectFrequencyType(planName) !== "monthly") return "";
      const match = String(planName || "").match(/\b([1-9]|1\d|2[0-8])(st|nd|rd|th)\b/i);
      if (!match) return "";
      const day = parseInt(match[1], 10);
      if (day < 1 || day > 28) return "";
      return match[2].toLowerCase() === ordinalSuffix(day) ? String(day) : "";
    }

    function normalizeFrequencyOptions() {
      if (!freqSelect) return;
      const selectOptions = Array.from(freqSelect.options || []);
      const hasAnchoredMonthlyOptions = selectOptions.some((optionEl) =>
        extractMonthlyAnchorFromPlanName(optionEl.textContent.trim()),
      );
      if (!hasAnchoredMonthlyOptions) return;

      let replacement = null;
      selectOptions.forEach((optionEl) => {
        const planName = optionEl.textContent.trim();
        const isGenericMonthly =
          detectFrequencyType(planName) === "monthly" &&
          !extractMonthlyAnchorFromPlanName(planName);
        if (!isGenericMonthly) {
          replacement = replacement || optionEl;
          return;
        }
        const wasSelected = optionEl.selected;
        optionEl.hidden = true;
        optionEl.disabled = true;
        if (wasSelected) {
          optionEl.selected = false;
        }
      });

      if (!freqSelect.selectedOptions.length && replacement) {
        replacement.selected = true;
      }
    }

    // Derive default anchor value from the current date (spec § 7.1)
    function defaultAnchorForType(freqType) {
      const now = new Date();
      if (freqType === "monthly") {
        // Day-of-month capped at 28 per spec § 3.2
        return String(Math.min(now.getDate(), 28));
      }
      // Weekday: JS getDay() 0=Sun … 6=Sat → spec 1=Mon … 5=Fri
      const jsDay = now.getDay(); // 0-6
      // Map: Mon=1, Tue=2, Wed=3, Thu=4, Fri=5; Sat/Sun → next Monday (1)
      return String(jsDay >= 1 && jsDay <= 5 ? jsDay : 1);
    }

    // Filter anchor options based on frequency type
    function updateAnchorOptions(freqType) {
      if (!anchorSelect) return;
      const opts = anchorSelect.options;
      const needType = freqType === "monthly" ? "monthday" : "weekday";
      const anchoredMonthlyDay =
        freqType === "monthly"
          ? extractMonthlyAnchorFromPlanName(getSelectedPlanName())
          : "";
      const defaultVal = anchoredMonthlyDay || defaultAnchorForType(freqType);
      let firstVisible = null;
      let hasSelectedVisible = false;
      for (let i = 0; i < opts.length; i++) {
        const anchorType = opts[i].getAttribute("data-anchor-type");
        const show =
          anchorType === needType &&
          (!anchoredMonthlyDay || opts[i].value === anchoredMonthlyDay);
        opts[i].hidden = !show;
        opts[i].disabled = !show;
        if (show && !firstVisible) firstVisible = opts[i];
        if (show && opts[i].selected) hasSelectedVisible = true;
      }
      const applyDefault = !anchorUserSet || !hasSelectedVisible;
      if (applyDefault) {
        let matched = false;
        for (let i = 0; i < opts.length; i++) {
          if (!opts[i].hidden && opts[i].value === defaultVal) {
            opts[i].selected = true;
            matched = true;
            break;
          }
        }
        if (!matched && firstVisible) firstVisible.selected = true;
      } else if (!hasSelectedVisible && firstVisible) {
        firstVisible.selected = true;
      }
    }

    // Compute the next occurrence of the anchor day for the schedule summary.
    // Spec §10.1: weekly ≥7 days, biweekly ≥14 days, monthly next valid day ≥7 days out.
    function computeNextOrderDate(freqType, anchorVal) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayMs = 86400000;
      let next;

      if (freqType === "monthly") {
        const dayOfMonth = parseInt(anchorVal, 10) || 1;
        next = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);
        if (next <= today) {
          next = new Date(today.getFullYear(), today.getMonth() + 1, dayOfMonth);
        }
        while (next.getTime() - today.getTime() < 7 * dayMs) {
          next = new Date(next.getFullYear(), next.getMonth() + 1, dayOfMonth);
        }
      } else {
        const targetDay = parseInt(anchorVal, 10) || 1; // 1=Mon … 5=Fri
        const jsToday = today.getDay(); // 0=Sun … 6=Sat
        let diff = targetDay - jsToday;
        if (diff <= 0) diff += 7;
        next = new Date(today.getTime() + diff * dayMs);
        const minLeadDays = freqType === "biweekly" ? 14 : 7;
        while (next.getTime() - today.getTime() < minLeadDays * dayMs) {
          next = new Date(next.getTime() + 7 * dayMs);
        }
      }

      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return months[next.getMonth()] + " " + next.getDate() + ", " + next.getFullYear();
    }

    // Sync the Delivery anchor line-item property hidden input
    // Important: do NOT submit an empty property for one-time purchases.
    // Shopify line-item merging is sensitive to property presence; an empty
    // "Delivery anchor" on one form and an absent property on another can
    // prevent expected merge behavior for identical variants.
    function syncAnchorProperty(isSubscribe) {
      if (!form) return;
      let anchorInput = form.querySelector('input[name="properties[Delivery anchor]"]');

      if (!isSubscribe) {
        if (anchorInput) anchorInput.remove();
        return;
      }

      if (!anchorSelect) return;
      if (!anchorInput) {
        anchorInput = document.createElement("input");
        anchorInput.type = "hidden";
        anchorInput.name = "properties[Delivery anchor]";
        form.appendChild(anchorInput);
      }

      const freqType = detectFrequencyType(getSelectedPlanName());
      const val = anchorSelect.value || "";
      if (freqType === "monthly") {
        anchorInput.value = val;
      } else {
        anchorInput.value = weekdayNames[parseInt(val, 10)] || val;
      }
    }

    // Update schedule summary text (spec § 7.1: "Every [freq] on [anchor]. Next order: [date].")
    function updateSummary(isSubscribe) {
      if (!summaryEl) return;
      if (!isSubscribe || !freqSelect) {
        summaryEl.hidden = true;
        summaryEl.textContent = "";
        return;
      }
      const freqType = detectFrequencyType(getSelectedPlanName());
      const freqLabel = freqType === "monthly" ? "Every month" : freqType === "biweekly" ? "Every 2 weeks" : "Every week";
      const anchorVal = anchorSelect ? anchorSelect.value : "";
      let anchorLabel = "";
      if (freqType === "monthly") {
        anchorLabel = "the " + anchorVal + ordinalSuffix(anchorVal);
      } else {
        anchorLabel = weekdayNames[parseInt(anchorVal, 10)] || "Monday";
      }
      const nextDate = computeNextOrderDate(freqType, anchorVal);
      summaryEl.textContent = freqLabel + " on " + anchorLabel + ". Next order: " + nextDate + ".";
      summaryEl.hidden = false;
    }

    const sync = () => {
      const checked = qs("[data-pma-sp-radio]:checked", container);
      if (!checked) return;
      const isSubscribe = checked.value === "subscribe";
      const freqType = freqSelect ? detectFrequencyType(getSelectedPlanName()) : "weekly";
      const anchoredMonthlyDay =
        isSubscribe && freqType === "monthly"
          ? extractMonthlyAnchorFromPlanName(getSelectedPlanName())
          : "";

      // Show/hide frequency chooser
      if (freqWrap) {
        freqWrap.hidden = !isSubscribe;
      }

      // Show/hide anchor day selector
      if (anchorWrap) {
        anchorWrap.hidden = !isSubscribe || Boolean(anchoredMonthlyDay);
      }

      // Update anchor options based on selected frequency
      if (isSubscribe && freqSelect) {
        updateAnchorOptions(freqType);
      }

      // Update hidden selling_plan input
      if (hiddenInput) {
        hiddenInput.value =
          isSubscribe && freqSelect ? freqSelect.value : "";
      }

      // Sync anchor day as line-item property
      syncAnchorProperty(isSubscribe);

      // Update schedule summary
      updateSummary(isSubscribe);

      // Toggle selected visual state
      options.forEach((opt) => {
        const v = opt.getAttribute("data-pma-sp-option");
        opt.classList.toggle(
          "pma-selling-plan--selected",
          (isSubscribe && v === "subscribe") ||
            (!isSubscribe && v === "onetime"),
        );
      });
    };

    radios.forEach((r) => r.addEventListener("change", sync));
    if (freqSelect) {
      freqSelect.addEventListener("change", () => {
        if (hiddenInput) hiddenInput.value = freqSelect.value;
        // Re-detect frequency type and update anchor options
        updateAnchorOptions(detectFrequencyType(getSelectedPlanName()));
        syncAnchorProperty(true);
        updateSummary(true);
      });
    }
    if (anchorSelect) {
      anchorSelect.addEventListener("change", () => {
        anchorUserSet = true;
        syncAnchorProperty(true);
        updateSummary(true);
      });
    }

    // Initial state
    normalizeFrequencyOptions();
    sync();

    let purchaseMode = "";
    try {
      if (typeof URLSearchParams !== "undefined" && window.location && window.location.search) {
        purchaseMode = new URLSearchParams(window.location.search).get("purchase") || "";
      }
    } catch {}
    if (purchaseMode === "order-plan") {
      const subscribeRadio = qs(
        '[data-pma-sp-option="subscribe"] [data-pma-sp-radio]',
        container,
      );
      if (subscribeRadio) {
        subscribeRadio.checked = true;
        subscribeRadio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  });

  // Shopify privacy banner / external overlays:
  // Reserve bottom space so critical CTAs (sticky ATC, toasts, bottom-of-page actions) are not obscured.
  const BOTTOM_OVERLAY_VAR = "--pma-bottom-overlay";
  const overlaySelectors = [
    ".shopify-pc__banner__dialog",
    "[id='shopify-pc__banner']",
    "[id^='shopify-pc__banner']",
    ".shopify-pc__banner__wrapper",
    "shopify-privacy-banner",
    "shopify-pc-banner",
    "shopify-cookie-consent",
  ].join(",");

  const isVisibleEl = (el) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect?.();
    if (!rect || rect.width <= 0 || rect.height <= 0) return false;
    const cs = window.getComputedStyle?.(el);
    if (!cs) return true;
    if (cs.display === "none" || cs.visibility === "hidden") return false;
    return true;
  };

  const computeBottomOverlayPx = () => {
    const els = qsa(overlaySelectors);
    if (!els.length) return 0;

    const h = window.innerHeight || 0;
    if (!h) return 0;

    let max = 0;
    for (const el of els) {
      if (!isVisibleEl(el)) continue;
      const rect = el.getBoundingClientRect();

      // Ignore non-bottom overlays (e.g., centered modals / headers).
      if (rect.top < h * 0.5) continue;
      // Allow a bit of inset (banner often sits above the viewport bottom).
      if (rect.bottom < h - 64) continue;

      const overlap = Math.max(0, h - rect.top);
      if (overlap > max) max = overlap;
    }
    return Math.round(max);
  };

  const applyBottomOverlay = () => {
    const px = computeBottomOverlayPx();
    document.documentElement.style.setProperty(BOTTOM_OVERLAY_VAR, `${px}px`);
  };

  let overlayRaf = 0;
  const scheduleBottomOverlay = () => {
    if (overlayRaf) return;
    overlayRaf = window.requestAnimationFrame(() => {
      overlayRaf = 0;
      applyBottomOverlay();
    });
  };

  if (document.body) {
    // Initial measurement after load.
    scheduleBottomOverlay();

    window.addEventListener("resize", scheduleBottomOverlay, { passive: true });

    // Detect banner insertion/removal and style/class changes.
    const mo = new MutationObserver(scheduleBottomOverlay);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden"],
    });
  }

  // GH-5577: Shopify's 404 template often exposes request.path as /404 while the browser
  // still has the visitor's original missing URL in location.pathname.
  const init404RequestedPath = () => {
    if (!document.body?.classList.contains("template-404")) return;

    const blocks = qsa("[data-pma-404-path-pending], .pma-global-404__path");
    if (!blocks.length) return;

    let pathname = "";
    try {
      pathname = String(window.location.pathname || "").trim();
    } catch {
      pathname = "";
    }
    if (!pathname || pathname === "/" || pathname === "/404") return;

    const display =
      pathname.length > 120 ? `${pathname.slice(0, 117)}…` : pathname;

    for (const block of blocks) {
      const code = qs("[data-pma-404-requested-path]", block);
      if (!code) continue;
      code.textContent = display;
      block.hidden = false;
      block.removeAttribute("data-pma-404-path-pending");
    }
  };

  init404RequestedPath();

  // GH-6417: copy-to-clipboard for support-facing reference codes.
  const initReferenceCopy = () => {
    const copiedText = "Copied";
    const defaultCopyText = "Copy";
    const resetCopied = (btn) => {
      const label = qs("[data-pma-reference-copy-label]", btn);
      if (!label) return;
      label.textContent = defaultCopyText;
      btn.classList.remove("is-copied");
      btn.removeAttribute("data-pma-reference-copied");
    };

    const writeClipboard = async (value) => {
      if (!value) return false;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          return true;
        }
      } catch {
        /* fall through */
      }
      try {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand("copy");
        textarea.remove();
        return ok;
      } catch {
        return false;
      }
    };

    document.addEventListener("click", async (event) => {
      const btn = event.target.closest("[data-pma-reference-copy]");
      if (!btn) return;
      event.preventDefault();

      const row = btn.closest("[data-pma-reference-row]");
      const valueEl = row?.querySelector("[data-pma-reference-value]");
      const value =
        btn.getAttribute("data-pma-reference-value") ||
        valueEl?.textContent?.trim() ||
        "";
      if (!value) return;

      const ok = await writeClipboard(value);
      if (!ok) return;

      const label = qs("[data-pma-reference-copy-label]", btn);
      if (label) label.textContent = copiedText;
      btn.classList.add("is-copied");
      btn.setAttribute("data-pma-reference-copied", "true");

      window.setTimeout(() => resetCopied(btn), 2000);
    });
  };

  initReferenceCopy();

  /* APA-RGR-003B (#7288): show clear control only when search row has text */
  const initHeaderSearchClear = () => {
    document.querySelectorAll("[data-pma-header-search-input]").forEach((input) => {
      const form = input.closest("form");
      const clearBtn = form?.querySelector("[data-pma-header-search-clear]");
      if (!clearBtn) return;
      const sync = () => {
        const hasText = Boolean(String(input.value || "").trim());
        clearBtn.hidden = !hasText;
      };
      input.addEventListener("input", sync);
      input.addEventListener("change", sync);
      clearBtn.addEventListener("click", (event) => {
        event.preventDefault();
        input.value = "";
        sync();
        input.focus();
        input.dispatchEvent(new Event("input", { bubbles: true }));
      });
      sync();
    });
  };
  initHeaderSearchClear();
})();
